<template>
    <div class="bg-slate-50 rounded-2xl border border-solid border-slate-200 p-4 sm:p-5">
        <div class="text-slate-700 text-base font-extrabold mb-3">{{ title }}</div>

        <div v-if="fields" class="@container">
            <div class="grid grid-cols-1 @xs:grid-cols-[132px_1fr] items-center text-slate-800 text-sm">
                <template v-for="[key, label] in visibleFields" :key="key">
                    <div class="break-all text-xs text-slate-500 @xs:pb-2">{{ label }}:</div>
                    <div class="break-all pb-2">
                        <template v-if="isKeyValueObject(props.event.data[key])">
                            <div
                                v-for="(val, subKey) in parseObject(props.event.data[key])"
                                :key="subKey"
                                class="text-sm"
                            >
                                <span class="text-slate-500">{{ subKey }}:</span> {{ val }}
                            </div>
                        </template>
                        <template v-else>{{ formatValue(props.event.data[key]) }}</template>
                    </div>
                </template>
            </div>
        </div>
        <div v-else>
            <vx-code-editor :modelValue="fallbackContent" disabled></vx-code-editor>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { VxCodeEditor } from '@voxie/frontend-components';
import { computed } from 'vue';

interface IntegrationLogEvent {
    type: string;
    data: Record<string, unknown>;
}

interface EventTypeConfig {
    title: string;
    fields: Record<string, string>;
}

const EVENT_TYPES: Record<string, EventTypeConfig> = {
    'Voxie\\Events\\Contact\\Captured': {
        title: 'Contact Captured',
        fields: {
            contactPhoneNumber: 'Phone',
            contactEmail: 'Email',
            contactFirstName: 'First Name',
            contactLastName: 'Last Name',
            contactTimeZone: 'Time Zone',
            contactEntryChannel: 'Entry Channel',
            contactMembershipStatus: 'Membership Status',
            contactLifecycleStatus: 'Lifecycle Status',
            groupId: 'Group ID',
            tags: 'Tags',
            customAttributes: 'Custom Attributes',
            externalIds: 'External IDs',
            subscriptionStatuses: 'Subscription Statuses',
            entryPoint: 'Entry Point',
            source: 'Source',
        },
    },
    'Voxie\\Events\\Conversion\\Captured': {
        title: 'Conversion Captured',
        fields: {
            conversionId: 'Conversion ID',
            conversionAt: 'Conversion Date',
            conversionType: 'Type',
            conversionSubtype: 'Subtype',
            conversionCategory: 'Category',
            conversionValue: 'Value',
            conversionItems: 'Items',
            customerPhone: 'Customer Phone',
            customerEmail: 'Customer Email',
            discountCodes: 'Discount Codes',
            vendorName: 'Vendor Name',
            vendorId: 'Vendor ID',
            groupId: 'Group ID',
            source: 'Source',
        },
    },
    'Voxie\\Events\\ExternalContactList\\Captured': {
        title: 'External Contact List Captured',
        fields: {
            name: 'Name',
            vendor: 'Vendor',
            vendorId: 'Vendor ID',
        },
    },
};

const props = defineProps<{
    event: IntegrationLogEvent;
}>();

const config = computed(() => EVENT_TYPES[props.event.type] ?? null);
const title = computed(() => config.value?.title ?? shortType(props.event.type));
const fields = computed(() => config.value?.fields ?? null);

const visibleFields = computed(() => {
    if (null === fields.value) {
        return [];
    }

    return Object.entries(fields.value).filter(([key]) => {
        const value = props.event.data[key];
        return null !== value && undefined !== value;
    });
});

function shortType(type: string): string {
    const parts = type.split('\\');
    return parts.slice(-2).join(' ');
}

function parseObject(value: unknown): Record<string, unknown> | null {
    if ('object' === typeof value && null !== value && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }

    if ('string' === typeof value) {
        try {
            const parsed = JSON.parse(value);
            if ('object' === typeof parsed && null !== parsed && !Array.isArray(parsed)) {
                return parsed as Record<string, unknown>;
            }
        } catch {
            // not JSON
        }
    }

    return null;
}

function isKeyValueObject(value: unknown): boolean {
    return null !== parseObject(value);
}

function formatValue(value: unknown): string {
    if (null === value || undefined === value) {
        return '-';
    }

    if (Array.isArray(value)) {
        return 0 === value.length ? '-' : value.join(', ');
    }

    if ('object' === typeof value) {
        return JSON.stringify(value);
    }

    return String(value);
}

const fallbackContent = computed(() => JSON.stringify(props.event.data, null, 4));
</script>
